import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Collapse,
  Stack,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Markdown from '../components/widgets/Markdown';
import { Colors } from '../styles';

const StepViewer = ({ steps = [], showVerbal = true, parts = [], itemId, itemPk }) => {
  const navigate = useNavigate();
  const [expandedSteps, setExpandedSteps] = useState({});

  // Initialize all steps as expanded
  useEffect(() => {
    const initialExpanded = {};
    const setExpandedState = (steps, pathPrefix = '') => {
      steps.forEach((step, index) => {
        const stepId = pathPrefix ? `${pathPrefix}_${index}` : `step_${index}`;
        initialExpanded[stepId] = true;
        if (step.steps && step.steps.length > 0) {
          setExpandedState(step.steps, stepId);
        }
      });
    };
    setExpandedState(steps);
    setExpandedSteps(initialExpanded);
  }, [steps]);

  const toggleStep = (stepId) => {
    setExpandedSteps(prev => ({
      ...prev,
      [stepId]: !prev[stepId]
    }));
  };

  const getStepTitle = (step) => {
    // First try to get the part name if part_id exists
    if (step.part_id) {
      const part = parts[step.part_id];
      if (part) {
        return part.title || part.name || `Part ${part.id}`;
      }
    }
    // Fall back to step's own title/name
    return step.title || step.name || "";
  };

  const renderStep = (step, index, pathPrefix = '') => {
    if (!step) return null;

    const stepId = pathPrefix ? `${pathPrefix}_${index}` : `step_${index}`;
    const isExpanded = expandedSteps[stepId];
    
    // Get the display title using the new helper function
    const displayTitle = getStepTitle(step);

    return (
      <Paper
        key={`step-${stepId}`}
        sx={{
          p: 1.5,
          mb: 1,
          backgroundColor: 'white',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box display="flex" alignItems="center" mb={1}>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {displayTitle}
          </Typography>
          {(step.steps?.length > 0 || step.verbal || step.note || step.endnote) && (
            <IconButton size="small" onClick={() => toggleStep(stepId)}>
              {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>

        <Collapse in={isExpanded}>
          <Stack spacing={1}>
            {step.description && (
              <Typography component="div" variant="body2" color="text.secondary">
                <Markdown text={step.description} />
              </Typography>
            )}

            {step.note && (
              <Typography component="div" variant="body2" sx={{mt: 1 }}>
                <Markdown text={step.note} />
              </Typography>
            )}
            {step.question && (
              <Typography component="div" variant="body2" sx={{mt: 1 }}>
                {step.question}
              </Typography>
            )}
            {step.notes && step.notes.length > 0 && (
              <Box sx={{mt: 1}}>
                {step.notes.map((note, index) => (
                  <Typography key={index} component="div" variant="body2">
                    {note}
                  </Typography>
                ))}
              </Box>
            )}
            {step.verbal && showVerbal && (
              <Box sx={{ 
                bgcolor: 'background.default',
                borderRadius: 1,
              }}>
                <Typography component="div" variant="body2" sx={{ 
                  backgroundColor: 'rgba(0, 0, 0, 0.03)', 
                  p: 1, 
                  borderRadius: 1 
                }}>
                  <Markdown text={step.verbal} />
                </Typography>
              </Box>
            )}


            <Box display="flex" gap={1} flexWrap="wrap">
              {step.hook && (
                <Chip 
                  size="small" 
                  label={`Hook: ${step.hook}`}
                  variant="outlined"
                />
              )}
              {step.needles && (
                <Chip 
                  size="small" 
                  label={`Needles: ${step.needles}`}
                  variant="outlined"
                />
              )}
              {step.yarn && (
                <Chip 
                  size="small" 
                  label={`Yarn: ${step.yarn}`}
                  variant="outlined"
                />
              )}
              {step.facing && (
                <Chip 
                  size="small" 
                  label={step.facing}
                  sx={{ 
                    color: step.facing === 'RS' ? Colors.DarkBlue : Colors.Magenta,
                    borderColor: step.facing === 'RS' ? Colors.DarkBlue : Colors.Magenta,
                  }}
                  variant="outlined"
                />
              )}
              {step.repeat > 0 && (
                <Chip 
                  size="small" 
                  label={`Repeat: ${step.repeat}`}
                  variant="outlined"
                />
              )}
              {step.part_id && (
                <Chip 
                  size="small" 
                  label={`Part: ${step.part_id}`}
                  variant="outlined"
                  sx={{ 
                    color: 'maroon',
                    borderColor: 'maroon',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(128, 0, 0, 0.04)'
                    }
                  }}
                  onClick={() => navigate(`/workspaces/${itemPk}/${itemId}/parts/${step.part_id}`)}
                />
              )}
            </Box>

            {step.steps && step.steps.length > 0 && (
              <Box sx={{ mt: 1, pl: 2 }}>
                {step.steps.map((subStep, subIndex) =>
                  renderStep(subStep, subIndex, stepId)
                )}
              </Box>
            )}

            {step.endnote && (
              <Typography component="div" variant="body2" sx={{ mt: 1 }}>
                <Markdown text={step.endnote} />
              </Typography>
            )}
          </Stack>
        </Collapse>
      </Paper>
    );
  };

  return (
    <Box>
      {steps.map((step, index) => renderStep(step, index))}
    </Box>
  );
};

export default StepViewer; 